import React, { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Criterion,
  Field,
  LogicJumpActionType,
  Quota,
  StudyType,
  useSession,
} from 'queries';
import { DeletableItem } from 'components/DeletableItem';
import { SquareButton } from '@app/core';
import { FieldPreview } from './FieldPreview';
import { FieldBuilder } from './FieldBuilder';
import { LogicJumpOption } from './elements/interface';
import { useOutsideClick } from 'hooks/useOutsideClick';

const useStyles = createUseStyles((theme: Theme) => ({
  topButton: {
    marginBottom: theme.marginBase,
    [theme.media.tablet]: {
      marginBottom: 0,
      marginRight: theme.marginBase,
    },
  },

  leftContainer: {
    position: 'absolute',
    left: -theme.marginBase * 7,
    top: 0,
    opacity: 0,
    '&:hover, &:focus, &:active': {
      opacity: 100,
    },
    [theme.media.tablet]: {
      opacity: 100,
      left: theme.marginBase * 2,
      right: 0,
      top: theme.marginBase * 2,
      display: 'flex',
      width: '50%',
    },
  },
  hiddenOnMobile: {
    [theme.media.mobile]: {
      display: 'none',
    },
  },
  mobileMarginTop: {
    [theme.media.mobile]: {
      marginTop: theme.marginBase * 3,
    },
  },
}));

interface Props {
  field: Field;
  onDelete?: (index: number) => any;
  onDuplicate?: (index: number) => any;
  onMoveUp?: (index: number) => any;
  onMoveDown?: (index: number) => any;
  onSelect?: (ref?: string) => any;
  onChange: (field: Field, index: number) => any;
  selected?: boolean;
  index: number;
  criterion?: Criterion;
  onCriterionChange?: (criterion: Criterion) => any;
  onRemoveCriterion?: (criterion: Criterion) => any;
  fieldNumber?: number;
  logicJumpOptions?: LogicJumpOption[];
  isLastField?: boolean;
  sessionId?: string;
  numberOfParticipantRequired?: number;
  quotas?: Quota[];
  onQuotaChange?: (quotas: Quota[]) => any;
}

export const RemovableCustomFormField = memo(
  ({
    field,
    onDelete,
    onDuplicate,
    selected,
    onMoveUp,
    onMoveDown,
    onSelect,
    onChange,
    index,
    criterion,
    onCriterionChange,
    onRemoveCriterion,
    fieldNumber,
    logicJumpOptions,
    isLastField,
    sessionId,
    numberOfParticipantRequired,
    quotas,
    onQuotaChange,
  }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });

    const { data: session } = useSession(sessionId);
    const onFieldChange = useCallback(
      (value: Field) => onChange(value, index),
      [onChange, index],
    );
    const _onSelect = useCallback(
      () => onSelect && onSelect(field.ref),
      [onSelect, field.ref],
    );
    const _onDelete = useCallback(
      () => onDelete && onDelete(index),
      [onDelete, index],
    );
    const _onDuplicate = useCallback(
      () => onDuplicate && onDuplicate(index),
      [onDuplicate, index],
    );

    const _onDeselect = useCallback(() => {
      onSelect && selected && onSelect(undefined);
    }, [onSelect, selected]);

    const { ref } = useOutsideClick(_onDeselect);

    const logicOptions = useMemo(() => {
      const number = fieldNumber || 0;
      return logicJumpOptions?.filter((o) => {
        if (o.number === undefined) {
          if (isLastField && o.type === LogicJumpActionType.END_FORM) {
            return false;
          }

          return true;
        }
        return o.number > number;
      });
    }, [logicJumpOptions, fieldNumber, isLastField]);

    return (
      <DeletableItem
        onDeleteClick={_onDelete}
        topButtons={
          <React.Fragment>
            <SquareButton
              icon="ri-edit-line"
              color="blue"
              className={classes.topButton}
              onClick={_onSelect}
              title="Edit"
            />
            <SquareButton
              icon="ri-file-copy-line"
              color="ink"
              className={classes.topButton}
              onClick={_onDuplicate}
              title="duplicate"
              variant="muted"
            />
          </React.Fragment>
        }
        containerClassName={classes.mobileMarginTop}
        isHiddenOnMobile={!selected}
        ref={ref}
      >
        <div
          className={classNames(classes.leftContainer, {
            [classes.hiddenOnMobile]: !selected,
          })}
        >
          <SquareButton
            icon="ri-arrow-up-s-line"
            color="inkSecondary"
            onClick={() => onMoveUp && onMoveUp(index)}
            className={classes.topButton}
            disabled={!onMoveUp}
          />
          <SquareButton
            icon="ri-arrow-down-s-line"
            color="inkSecondary"
            onClick={() => onMoveDown && onMoveDown(index)}
            disabled={!onMoveDown}
          />
        </div>
        {selected ? (
          <FieldBuilder
            field={field}
            onChange={onFieldChange}
            fieldNumber={fieldNumber}
            criterion={criterion}
            onCriterionChange={onCriterionChange}
            onRemoveCriterion={onRemoveCriterion}
            logicJumpOptions={logicOptions}
            shouldAlwaysValidate={session?.studyType === StudyType.UNMODERATED}
            numberOfParticipantRequired={numberOfParticipantRequired}
            sessionId={sessionId}
            quotas={quotas}
            onQuotaChange={onQuotaChange}
          />
        ) : (
          <FieldPreview
            field={field}
            onChange={onFieldChange}
            fieldNumber={fieldNumber}
            criterion={criterion}
            onCriterionChange={onCriterionChange}
            logicJumpOptions={logicOptions}
            isLastField={isLastField}
            shouldAlwaysValidate={session?.studyType === StudyType.UNMODERATED}
            planId={session?.planId}
            numberOfParticipantRequired={numberOfParticipantRequired}
            sessionId={sessionId}
            quotas={quotas}
          />
        )}
      </DeletableItem>
    );
  },
);
